/* =====================================================================
   BUBBASAFE — Shopify site-wide behaviours (single IIFE, zero deps)
   (1) Reveal-on-scroll for [data-reveal], .reveal  -> add 'in' + 'is-visible'
   (2) Animated counters for .bb-proto .stat b, .js-countup[data-target],
       [data-count]
   (3) Accordion: delegated click on .bb-proto .acc__q -> toggles .is-open
       on the closest .acc (and sets an explicit max-height for animation)
   (4) Marquee: duplicate each .bb-proto .marquee__track once (seamless loop)
   (5) Dawn cart free-shipping nudge ($150 / 15000c)
   Defensive null-checks throughout; respects prefers-reduced-motion; every
   piece is idempotent (guard flags) so re-runs never double-bind/double-count.
   Re-runs on 'shopify:section:load' AND 'shopify:section:select' so it works
   live inside the Shopify theme editor.
   ===================================================================== */
(function (w, d) {
  'use strict';

  var reduce = !!(w.matchMedia && w.matchMedia('(prefers-reduced-motion: reduce)').matches);
  function nowMs() { return (w.performance && w.performance.now) ? w.performance.now() : Date.now(); }
  var hasIO = ('IntersectionObserver' in w);

  /* =================================================================
     (1) REVEAL ON SCROLL  --  [data-reveal], .reveal
     ================================================================= */
  var revealIO = null;
  function reveal(el) {
    el.classList.add('in');
    el.classList.add('is-visible');
  }
  function initReveals() {
    var els = [].slice.call(d.querySelectorAll('[data-reveal], .reveal'));
    if (!els.length) return;
    if (reduce || !hasIO) {
      els.forEach(function (el) {
        if (el.dataset.bbReveal) return;
        el.dataset.bbReveal = '1';
        reveal(el);
      });
      return;
    }
    if (!revealIO) {
      revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          revealIO.unobserve(en.target);
          reveal(en.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }
    els.forEach(function (el) {
      if (el.dataset.bbReveal) return;
      el.dataset.bbReveal = '1';
      revealIO.observe(el);
    });
  }

  /* =================================================================
     (2) ANIMATED COUNTERS
     --  .bb-proto .stat b, .js-countup[data-target], [data-count]
     A count target is read from data-target, then data-count, then the
     element's own text. Prefix/suffix from the text are preserved.
     ================================================================= */
  var countIO = null;
  function runCount(el) {
    if (el.dataset.bbCounted) return;
    var raw = el.getAttribute('data-target') || el.getAttribute('data-count') || el.textContent;
    var m = String(raw).match(/^(\D*)([\d.,]+)(.*)$/);
    if (!m) return; // no digits (e.g. "AU") -> leave unchanged
    el.dataset.bbCounted = '1';
    // Suffix comes from the dedicated data-suffix attribute when present (so "+"/"%"
    // survive the animation); otherwise fall back to trailing chars in the target.
    var pre = m[1], numStr = m[2].replace(/,/g, '');
    var suf = el.getAttribute('data-suffix');
    if (suf === null) suf = m[3];
    var target = parseFloat(numStr);
    if (isNaN(target)) return;
    var decimals = (numStr.split('.')[1] || '').length;
    function fmt(v) {
      return pre + v.toLocaleString('en-AU', {
        minimumFractionDigits: decimals, maximumFractionDigits: decimals
      }) + suf;
    }
    // Always land on the exact target — even if the rAF loop is throttled or
    // interrupted (backgrounded tab, fast scroll). Without this guard the counter
    // could freeze on a wrong intermediate value permanently (bbCounted blocks re-run).
    var done = false;
    function finish() { if (done) return; done = true; el.textContent = fmt(target); }
    if (reduce) { finish(); return; }
    var dur = 1400, start = nowMs();
    (function tick() {
      if (done) return;
      var t = Math.min(1, (nowMs() - start) / dur);
      var eased = 1 - Math.pow(1 - t, 3); // cubic ease-out
      el.textContent = fmt(target * eased);
      if (t < 1) requestAnimationFrame(tick); else finish();
    })();
    setTimeout(finish, dur + 500); // safety net if rAF never completes
  }
  function initCounters() {
    var els = [].slice.call(d.querySelectorAll('.bb-proto .stat b, .js-countup[data-target], [data-count]'));
    if (!els.length) return;
    if (!hasIO) { els.forEach(runCount); return; }
    if (!countIO) {
      countIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          countIO.unobserve(en.target);
          runCount(en.target);
        });
      }, { threshold: 0.4 });
    }
    els.forEach(function (el) {
      if (el.dataset.bbCountObs) return;
      el.dataset.bbCountObs = '1';
      countIO.observe(el);
    });
  }

  /* =================================================================
     (3) ACCORDION  --  delegated click on .bb-proto .acc__q
     Toggles .is-open on the closest .acc; CSS animates .acc__a via the
     class, and we also set an explicit pixel max-height so the transition
     is smooth regardless of content length.
     ================================================================= */
  var accBound = false;
  function bindAccordion() {
    if (accBound) return;
    accBound = true;
    // collapsed panels start out of the tab order / a11y tree (inert)
    d.querySelectorAll('.bb-proto .acc').forEach(function (acc) {
      var a = acc.querySelector('.acc__a');
      if (a) a.inert = !acc.classList.contains('is-open');
    });
    d.addEventListener('click', function (e) {
      var q = e.target && e.target.closest ? e.target.closest('.bb-proto .acc__q') : null;
      if (!q) return;
      var acc = q.closest('.acc');
      if (!acc) return;
      var a = acc.querySelector('.acc__a');
      var open = acc.classList.toggle('is-open');
      q.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (a) { a.style.maxHeight = open ? (a.scrollHeight + 40) + 'px' : ''; a.inert = !open; }
    });
  }

  /* =================================================================
     (4) MARQUEE  --  duplicate each .bb-proto .marquee__track once
     ================================================================= */
  function initMarquees() {
    d.querySelectorAll('.bb-proto .marquee__track').forEach(function (t) {
      if (t.dataset.bbDup) return;
      t.dataset.bbDup = '1';
      [].slice.call(t.children).forEach(function (node) {
        var clone = node.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        t.appendChild(clone);
      });
    });
  }

  /* =================================================================
     (5) FREE-SHIPPING NUDGE  (Dawn <cart-drawer>)
     Subscribe to Dawn 'cart-update' pub/sub, fetch /cart.js, inject a
     progress bar into <cart-drawer> .drawer__inner, remove when empty.
     ================================================================= */
  var FREE_SHIP_THRESHOLD = 15000; // cents = $150 AUD
  var BAR_ID = 'bubbasafe-ship-bar';
  var lastCart = null;
  var cartBound = false; // one-time subscribe/listener guard

  function root() {
    return (w.Shopify && w.Shopify.routes && w.Shopify.routes.root) || '/';
  }
  function moneyAUD(cents) {
    var v = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency', currency: 'AUD',
        minimumFractionDigits: v % 1 === 0 ? 0 : 2
      }).format(v);
    } catch (e) {
      return '$' + v.toFixed(2);
    }
  }

  function shipBarMarkup(cart) {
    var total = Number(cart && cart.total_price) || 0;
    var remaining = FREE_SHIP_THRESHOLD - total;
    var pct = Math.max(0, Math.min(100, (total / FREE_SHIP_THRESHOLD) * 100));
    var unlocked = remaining <= 0;
    var msg = unlocked
      ? ' <b>You&rsquo;ve unlocked FREE shipping!</b>'
      : 'You&rsquo;re <b>' + moneyAUD(remaining) + '</b> away from <b>FREE shipping</b>';
    return (
      '<div class="bubbasafe-ship-bar__msg" role="status" aria-live="polite">' + msg + '</div>' +
      '<div class="bubbasafe-ship-bar__track" aria-hidden="true">' +
        '<div class="bubbasafe-ship-bar__fill" style="width:' + pct.toFixed(1) + '%"></div>' +
      '</div>'
    );
  }

  function styleBar(bar) {
    // Inline defaults so it looks right without a separate stylesheet.
    bar.style.cssText =
      'padding:12px 16px;margin:0;border-bottom:1px solid rgba(var(--color-foreground,26,26,26),0.08);' +
      'font-size:1.3rem;line-height:1.3;';
    var fill = bar.querySelector('.bubbasafe-ship-bar__fill');
    var track = bar.querySelector('.bubbasafe-ship-bar__track');
    if (track) {
      track.style.cssText =
        'margin-top:8px;height:6px;border-radius:999px;overflow:hidden;' +
        'background:rgba(var(--color-foreground,26,26,26),0.12);';
    }
    if (fill) {
      fill.style.cssText =
        'height:100%;border-radius:999px;' +
        'background:rgb(var(--color-button,232,71,28));' +
        (reduce ? '' : 'transition:width .5s cubic-bezier(.22,1,.36,1);');
    }
  }

  function renderShipBar(cart) {
    if (cart) lastCart = cart;
    cart = lastCart;
    if (!cart) return;

    var drawer = d.querySelector('cart-drawer');
    if (!drawer) return;
    var inner = drawer.querySelector('.drawer__inner');
    if (!inner) return;

    var count = Number(cart.item_count) || 0;
    var existing = d.getElementById(BAR_ID);

    // Empty cart -> no nudge.
    if (count <= 0) { if (existing) existing.remove(); return; }

    if (!existing) {
      existing = d.createElement('div');
      existing.id = BAR_ID;
      existing.className = 'bubbasafe-ship-bar';
      var header = inner.querySelector('.drawer__header');
      if (header && header.parentNode) {
        header.parentNode.insertBefore(existing, header.nextSibling);
      } else {
        inner.insertBefore(existing, inner.firstChild);
      }
    }
    existing.innerHTML = shipBarMarkup(cart);
    styleBar(existing);
  }

  function fetchCart(cb) {
    if (!w.fetch) return;
    fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { renderShipBar(cart); if (cb) cb(cart); })
      .catch(function () {});
  }

  function syncFromEvent(evt) {
    var data = evt && (evt.cartData || evt.cart);
    if (data && typeof data.total_price !== 'undefined' && typeof data.item_count !== 'undefined') {
      // Dawn re-renders .drawer__inner async after publishing; wait a tick.
      setTimeout(function () { renderShipBar(data); }, 60);
    } else {
      setTimeout(function () { fetchCart(); }, 60);
    }
  }

  function bindCart() {
    // One-time global subscriptions (guarded so section reloads never
    // double-subscribe); the progress-bar refresh still runs every init.
    if (!cartBound) {
      cartBound = true;
      // Dawn pub/sub: subscribe(PUB_SUB_EVENTS.cartUpdate, cb) === 'cart-update'.
      if (typeof w.subscribe === 'function') {
        try { w.subscribe('cart-update', syncFromEvent); } catch (e) {}
      }
      // Backstop for older/other emitters + native events.
      d.addEventListener('cart:refresh', function () { fetchCart(); });
    }

    // Re-inject after Dawn wipes .drawer__inner innerHTML on re-render.
    var drawer = d.querySelector('cart-drawer');
    if (drawer && 'MutationObserver' in w && !drawer.__bubbaObs) {
      drawer.__bubbaObs = new MutationObserver(function () {
        if (lastCart && !d.getElementById(BAR_ID)) renderShipBar(lastCart);
      });
      drawer.__bubbaObs.observe(drawer, { childList: true, subtree: true });
    }
    fetchCart();
  }

  /* =================================================================
     (6) TESTIMONIAL CAROUSEL  --  .bb-proto .slider (.slider--3 / --2)
     ================================================================= */
  var sliderNavBound = false;
  function initSliders() {
    d.querySelectorAll('.bb-proto .slider').forEach(function (s) {
      if (s.__bbSlider) return;
      var track = s.querySelector('.slider__track');
      var slides = track ? track.children.length : 0;
      var dotsWrap = s.querySelector('.slider__dots');
      if (!track || !slides) return;
      var i = 0, timer = null;
      function per() {
        if (w.innerWidth >= 760 && s.classList.contains('slider--3')) return 3;
        if (w.innerWidth >= 760 && s.classList.contains('slider--2')) return 2;
        return 1;
      }
      function pages() { return Math.max(1, slides - per() + 1); }
      function render() {
        var pv = per();
        track.style.transform = 'translateX(' + (-(100 / pv) * i) + '%)';
        if (dotsWrap) {
          dotsWrap.innerHTML = '';
          for (var p = 0; p < pages(); p++) {
            var dot = d.createElement('button');
            dot.type = 'button';
            dot.className = 'slider__dot' + (p === i ? ' active' : '');
            dot.setAttribute('aria-label', 'Go to slide ' + (p + 1));
            (function (p) { dot.addEventListener('click', function () { i = p; render(); restart(); }); })(p);
            dotsWrap.appendChild(dot);
          }
        }
      }
      function go(dir) { var n = pages(); i = (i + dir + n) % n; render(); }
      function restart() { if (reduce) return; clearInterval(timer); timer = setInterval(function () { go(1); }, 5200); }
      s.__bbSlider = { go: function (dir) { go(dir); restart(); } };
      s.addEventListener('mouseenter', function () { clearInterval(timer); });
      s.addEventListener('mouseleave', restart);
      w.addEventListener('resize', function () { if (i > pages() - 1) i = pages() - 1; render(); });
      render(); restart();
    });
    if (!sliderNavBound) {
      sliderNavBound = true;
      d.addEventListener('click', function (e) {
        var arrow = e.target.closest ? e.target.closest('.slider__arrow') : null;
        if (!arrow || !arrow.closest('.bb-proto')) return;
        var s = arrow.closest('.slider');
        if (s && s.__bbSlider) s.__bbSlider.go(arrow.dataset.dir === 'next' ? 1 : -1);
      });
    }
  }

  /* =================================================================
     (7) BEFORE/AFTER COMPARE SLIDER  --  .bb-proto .compare (night vision)
     ================================================================= */
  function initCompare() {
    d.querySelectorAll('.bb-proto .compare').forEach(function (c) {
      if (c.dataset.bbCompare) return;
      c.dataset.bbCompare = '1';
      var knob = c.querySelector('.compare__knob');
      function apply(p) { p = Math.max(0, Math.min(100, p)); c.style.setProperty('--pos', p.toFixed(2) + '%'); if (knob) knob.setAttribute('aria-valuenow', Math.round(p)); }
      function set(x) { var r = c.getBoundingClientRect(); apply(((x - r.left) / r.width) * 100); }
      var dragging = false;
      c.addEventListener('pointerdown', function (e) { dragging = true; try { c.setPointerCapture(e.pointerId); } catch (_) {} set(e.clientX); });
      c.addEventListener('pointermove', function (e) { if (dragging) set(e.clientX); });
      c.addEventListener('pointerup', function () { dragging = false; });
      c.addEventListener('pointercancel', function () { dragging = false; });
      if (knob) {
        knob.setAttribute('tabindex', '0'); knob.setAttribute('role', 'slider');
        knob.setAttribute('aria-valuemin', '0'); knob.setAttribute('aria-valuemax', '100'); knob.setAttribute('aria-valuenow', '50');
        knob.setAttribute('aria-label', 'Reveal day or night view');
        knob.addEventListener('keydown', function (e) {
          var cur = parseFloat(getComputedStyle(c).getPropertyValue('--pos')) || 50;
          if (e.key === 'ArrowLeft') { apply(cur - 4); e.preventDefault(); }
          if (e.key === 'ArrowRight') { apply(cur + 4); e.preventDefault(); }
        });
      }
    });
  }

  /* =================================================================
     INIT + re-run on load / Shopify section swaps
     ================================================================= */
  function init() {
    initReveals();
    initCounters();
    bindAccordion();
    initMarquees();
    initSliders();
    initCompare();
    bindCart();
  }

  if (d.readyState === 'loading') {
    d.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor / AJAX section swaps: re-scan new nodes, re-bind cart bar.
  d.addEventListener('shopify:section:load', init);
  d.addEventListener('shopify:section:select', init);
})(window, document);